<template>
  <main class="rm-page finish-form-page">
    <header class="rm-topbar rm-topbar--back-only">
      <button
        class="rm-icon-button"
        type="button"
        aria-label="Volver"
        @click="goBack"
      >
        <span aria-hidden="true"></span>
      </button>

      <div class="rm-heading">
        <h1 class="rm-title">Finalizar parada</h1>
      </div>
    </header>

    <section class="finish-form-state-shell" aria-live="polite">
      <div v-if="isLoading" class="finish-form-state">
        <span class="finish-form-spinner" aria-hidden="true"></span>
        <p>Cargando parada...</p>
      </div>

      <div v-else-if="errorMessage" class="finish-form-state">
        <h2>No se pudo preparar la finalizacion</h2>
        <p>{{ errorMessage }}</p>
        <button
          class="rm-button rm-button--outline"
          type="button"
          :disabled="isLoading"
          @click="loadState"
        >
          Reintentar
        </button>
      </div>

      <section
        v-else
        class="finish-form-content"
        aria-label="Formulario de finalización"
        :aria-busy="isSubmitting"
      >
        <div class="finish-form-scroll">
          <p v-if="chipsText" class="rm-chips">{{ chipsText }}</p>

          <section
            v-if="completionMode === completionModes.direct"
            class="finish-form-stop-options"
            aria-label="Resultado de parada"
          >
            <button
              class="finish-form-option"
              :class="{
                'finish-form-option--selected':
                  selectedStopResult === stopResultValues.correct,
              }"
              type="button"
              @click="selectCorrect"
            >
              <span class="finish-form-option__icon-shell" aria-hidden="true">
                <span
                  class="finish-form-option__icon finish-form-option__icon--success"
                ></span>
              </span>
              <span class="finish-form-option__label">Correcto</span>
              <span
                class="finish-form-option__marker"
                :class="{
                  'finish-form-option__marker--active':
                    selectedStopResult === stopResultValues.correct,
                }"
                aria-hidden="true"
              ></span>
            </button>

            <button
              class="finish-form-option"
              :class="{
                'finish-form-option--selected':
                  selectedStopResult === stopResultValues.incident,
              }"
              type="button"
              @click="selectIncident"
            >
              <span class="finish-form-option__icon-shell" aria-hidden="true">
                <span
                  class="finish-form-option__icon finish-form-option__icon--warning"
                ></span>
              </span>
              <span class="finish-form-option__text">
                <span class="finish-form-option__label">Incidencia</span>
                <small
                  v-if="selectedIncidentDescription"
                  class="finish-form-option__detail"
                  >{{ selectedIncidentDescription }}</small
                >
              </span>
              <span
                class="finish-form-option__marker"
                :class="{
                  'finish-form-option__marker--active':
                    selectedStopResult === stopResultValues.incident,
                }"
                aria-hidden="true"
              ></span>
            </button>
          </section>

          <section class="finish-form-observations">
            <label for="finish-observations">OBSERVACIONES</label>
            <textarea
              id="finish-observations"
              v-model="observations"
              maxlength="100"
              rows="5"
            ></textarea>
          </section>

          <p
            v-if="submitMessage"
            class="finish-form-message"
            :class="`finish-form-message--${submitMessageType}`"
          >
            {{ submitMessage }}
          </p>
        </div>

        <footer class="finish-form-footer">
          <button
            class="rm-button finish-form-footer__submit"
            type="button"
            :disabled="!isFinalizeEnabled"
            @click="submitFinalize"
          >
            <img
              class="rm-button__icon-img"
              :src="sendIcon"
              alt=""
              aria-hidden="true"
            />
            <span>Finalizar Parada</span>
          </button>
        </footer>

        <div
          v-if="isSubmitting"
          class="finish-form-loading-overlay"
          role="status"
          aria-live="polite"
        >
          <span class="finish-form-spinner" aria-hidden="true"></span>
          <p>Finalizando parada...</p>
        </div>
      </section>
    </section>

    <IncidentSelectionModal
      :open="showIncidentSelectionModal"
      :loading="false"
      :options="incidentOptions"
      @cancel="showIncidentSelectionModal = false"
      @select="selectIncidentOption"
    />
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { mobileRouteStore } from "@/_store/mobileRouteStore";
import sendIcon from "../../../../assets/img/mobile-images/send.svg";
import IncidentSelectionModal from "../shared/IncidentSelectionModal.vue";
import { loadRouteStopDetail } from "../shared/loadRouteStopDetail";
import { normalizeStoredIncidentEvents } from "../scan-packages/scanPackagesModel";
import {
  buildFinishChips,
  buildRouteStopIncidentOptions,
  canFinalize,
  completionModes,
  createFinalizePayload,
  normalizeStoredRouteStopContext,
  parseCompletionMode,
  stopResultValues,
} from "./finishFormModel";

const props = defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
  context: {
    type: Object,
    default() {
      return null;
    },
  },
});
const emit = defineEmits(["back", "completed"]);
const mobileRoutesStore = mobileRouteStore();
const planId = ref("");
const routeStopId = ref("");
const completionMode = ref(completionModes.direct);
const routeStop = ref(null);
const taskScanDraft = ref(null);
const selectedStopResult = ref(stopResultValues.correct);
const selectedIncidentCode = ref("");
const observations = ref("");
const isLoading = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref("");
const submitMessage = ref("");
const submitMessageType = ref("info");
const showIncidentSelectionModal = ref(false);
let abortController = null;

const stop = computed(() => routeStop.value?.stop ?? null);
const chipsText = computed(() =>
  buildFinishChips({
    routeStop: routeStop.value,
    draft: taskScanDraft.value,
  }).join(" · ")
);
const contextIncidentEvents = computed(() =>
  normalizeStoredIncidentEvents(props.context?.incidentEvents || [])
);
const incidentEvents = computed(() =>
  Array.isArray(mobileRoutesStore.incidentEvents) &&
  mobileRoutesStore.incidentEvents.length
    ? mobileRoutesStore.incidentEvents
    : contextIncidentEvents.value
);
const incidentOptions = computed(() =>
  buildRouteStopIncidentOptions(incidentEvents.value, routeStop.value)
);
const selectedIncident = computed(() => {
  if (selectedStopResult.value !== stopResultValues.incident) {
    return null;
  }

  if (incidentOptions.value.length) {
    const incident = incidentOptions.value.find(
      (option) => option.code === selectedIncidentCode.value
    );
    return incident
      ? { code: incident.code, description: incident.description }
      : null;
  }
  return null;
});
const selectedIncidentDescription = computed(
  () => selectedIncident.value?.description ?? ""
);
const isFinalizeEnabled = computed(() =>
  canFinalize({
    isSubmitting: isSubmitting.value,
    routeStop: routeStop.value,
    completionMode: completionMode.value,
    selectedStopResult: selectedStopResult.value,
    selectedIncident: selectedIncident.value,
    draft: taskScanDraft.value,
  })
);

onMounted(() => {
  readRouteQuery();
  loadState();
});

onBeforeUnmount(() => {
  abortCurrentRequest();
});

async function loadState() {
  if (isLoading.value) {
    return;
  }

  if (!routeStopId.value) {
    errorMessage.value = "No se recibio la parada en la URL.";
    return;
  }

  abortCurrentRequest();
  abortController = new AbortController();

  isLoading.value = true;
  errorMessage.value = "";
  submitMessage.value = "";

  try {
    taskScanDraft.value =
      completionMode.value === completionModes.taskScan
        ? props.context?.completionDraft ?? null
        : null;

    if (taskScanDraft.value?.observations) {
      observations.value = taskScanDraft.value.observations.slice(0, 200);
    }

    routeStop.value = normalizeStoredRouteStopContext(
      props.context?.routeStop ?? null
    );

    if (!routeStop.value && planId.value) {
      routeStop.value = await loadRouteStopDetail({
        routeStop: props.context?.routeStop ?? null,
        planId: planId.value,
        routeStopId: routeStopId.value,
        vehicleId: (props.context?.vehicleId || "").toString(),
        signal: abortController.signal,
      });
    }

    if (!routeStop.value) {
      errorMessage.value = "No se encontro la parada seleccionada.";
    }
  } catch (error) {
    if (error.name === "AbortError") {
      return;
    }

    errorMessage.value = error.message || "No se pudo cargar la parada.";
  } finally {
    isLoading.value = false;
    abortController = null;
  }
}

function selectCorrect() {
  selectedStopResult.value = stopResultValues.correct;
  selectedIncidentCode.value = "";
  showIncidentSelectionModal.value = false;
}

function selectIncident() {
  selectedStopResult.value = stopResultValues.incident;
  showIncidentSelectionModal.value = true;
}

function selectIncidentOption(incident) {
  if (!incident?.code) {
    return;
  }

  selectedIncidentCode.value = incident.code;
  showIncidentSelectionModal.value = false;
}

async function submitFinalize() {
  if (!isFinalizeEnabled.value) {
    return;
  }

  const effectivePlanId =
    stop.value?.planId || taskScanDraft.value?.planId || planId.value;
  const effectiveRouteStopId =
    stop.value?.id || taskScanDraft.value?.routeStopId || routeStopId.value;

  if (!effectivePlanId || !effectiveRouteStopId) {
    submitMessage.value = "No se pudo resolver el plan o la parada.";
    submitMessageType.value = "warning";
    return;
  }

  abortCurrentRequest();
  abortController = new AbortController();

  isSubmitting.value = true;
  submitMessage.value = "";

  try {
    const payload = createFinalizePayload({
      completionMode: completionMode.value,
      selectedStopResult: selectedStopResult.value,
      incident: selectedIncident.value,
      observations: observations.value,
      draft: taskScanDraft.value,
    });

    await mobileRoutesStore.finalizeRouteStop({
      planId: effectivePlanId,
      routeStopId: effectiveRouteStopId,
      mode: completionMode.value,
      payload,
      signal: abortController.signal,
    });
    submitMessage.value = "Parada finalizada correctamente.";
    submitMessageType.value = "success";

    if (props.embedded) {
      emit("completed", {
        planId: effectivePlanId,
        routeStopId: effectiveRouteStopId,
      });
      return;
    }

    const target = new URL("/", window.location.origin);
    target.searchParams.set("planId", effectivePlanId);
    window.location.assign(target.toString());
  } catch (error) {
    if (error.name === "AbortError") {
      return;
    }

    submitMessage.value = error.message || "No se pudo finalizar la parada.";
    submitMessageType.value = "warning";
  } finally {
    isSubmitting.value = false;
    abortController = null;
  }
}

function goBack() {
  if (props.embedded) {
    emit("back");
    return;
  }

  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  const target = new URL("/service-detail", window.location.origin);

  if (planId.value) {
    target.searchParams.set("planId", planId.value);
  }

  target.searchParams.set("stopId", routeStopId.value);
  window.location.assign(target.toString());
}

function readRouteQuery() {
  if (props.embedded) {
    planId.value = props.context?.planId?.trim?.() ?? "";
    routeStopId.value =
      (props.context?.routeStopId || props.context?.stopId || "").trim?.() ??
      "";
    completionMode.value = parseCompletionMode(
      props.context?.completionMode || props.context?.mode || ""
    );
    return;
  }

  const query = new URLSearchParams(window.location.search);
  planId.value = query.get("planId")?.trim() ?? "";
  routeStopId.value = query.get("stopId")?.trim() ?? "";
  completionMode.value = parseCompletionMode(
    query.get("mode") ?? query.get("completionMode") ?? ""
  );
}

function abortCurrentRequest() {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
}
</script>

<style scoped src="./finishForm.css"></style>
<style scoped src="../shared/routesMobileCommon.css"></style>

